import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  useGetManagedUserDeviceAlertPreferencesQuery,
  useUpdateManagedUserDeviceAlertPreferencesMutation,
} from "../../redux/admin/adminApi";
import { BrandDialogFooter, BrandDialogFrame } from "./wizard/BrandDialogs";

const normalizeFilterValue = (value) => String(value || "").trim().toLowerCase();

const getDeviceId = (device) => String(device?._id || device?.id || "");
const getDeviceName = (device) => device?.name || device?.vehicleNumber || device?.imei || "Device";

export default function ManagedUserNotificationDialog({
  open,
  onClose,
  targetUser,
  devices = [],
  showStatus,
  title = "User Notifications",
}) {
  const [search, setSearch] = useState("");
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [busyKey, setBusyKey] = useState("");

  const targetUserId = String(targetUser?._id || "");

  const assignedDevices = useMemo(() => {
    if (!targetUserId) return [];
    return (Array.isArray(devices) ? devices : []).filter((device) => {
      const assignedToUserId = String(device?.assignedToUser?._id || device?.assignedToUser || "");
      const assignedViewers = Array.isArray(device?.assignedToViewers)
        ? device.assignedToViewers.map((viewer) => String(viewer?._id || viewer))
        : [];
      return assignedToUserId === targetUserId || assignedViewers.includes(targetUserId);
    });
  }, [devices, targetUserId]);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setBusyKey("");
    setSelectedDeviceId((current) => {
      if (current && assignedDevices.some((device) => getDeviceId(device) === current)) {
        return current;
      }
      return getDeviceId(assignedDevices[0]);
    });
  }, [assignedDevices, open]);

  const {
    data: alertPayload,
    isFetching,
    refetch,
  } = useGetManagedUserDeviceAlertPreferencesQuery(
    { userId: targetUserId, deviceId: selectedDeviceId },
    { skip: !open || !targetUserId || !selectedDeviceId }
  );
  const [updatePreferences] = useUpdateManagedUserDeviceAlertPreferencesMutation();

  const rows = useMemo(() => {
    const base = Array.isArray(alertPayload?.alerts) ? alertPayload.alerts : [];
    const query = normalizeFilterValue(search);
    if (!query) return base;
    return base.filter((item) => normalizeFilterValue(item?.label).includes(query));
  }, [alertPayload, search]);

  const handleToggle = async (item, checked) => {
    if (!targetUserId || !selectedDeviceId) return;

    const sourceRows = Array.isArray(alertPayload?.alerts) ? alertPayload.alerts : [];
    const nextAlerts = sourceRows
      .map((row) => {
        if (row.alarmType === item.alarmType) {
          return { ...row, checked };
        }
        return row;
      })
      .filter((row) => row.checked)
      .map((row) => row.alarmType);

    setBusyKey(item.key);
    try {
      await updatePreferences({
        userId: targetUserId,
        deviceId: selectedDeviceId,
        alerts: nextAlerts,
      }).unwrap();
      await refetch();
      showStatus?.("success", `${item.label} notification ${checked ? "enabled" : "disabled"} for ${targetUser?.username || "user"}.`);
    } catch (error) {
      showStatus?.("error", error?.data?.msg || "Failed to update user notification preference.");
    } finally {
      setBusyKey("");
    }
  };

  return (
    <BrandDialogFrame
      open={open}
      onClose={busyKey ? undefined : onClose}
      title={title}
      subtitle={targetUser?.username || targetUser?.name || "Selected user"}
      tone="blue"
      maxWidth="sm"
      footer={
        <BrandDialogFooter
          onCancel={onClose}
          onConfirm={onClose}
          cancelLabel="Close"
          confirmLabel="Done"
          confirmTone="blue"
          disabled={Boolean(busyKey)}
        />
      }
    >
      <Stack spacing={2}>
        {alertPayload?.ownerUser?.username ? (
          <Typography sx={{ fontSize: 13.5, color: "#64748b", fontWeight: 700 }}>
            Templates controlled by {alertPayload.ownerUser.username}
          </Typography>
        ) : null}

        <TextField
          select
          fullWidth
          label="Assigned Device"
          value={selectedDeviceId}
          onChange={(event) => setSelectedDeviceId(event.target.value)}
          sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: 1.5 } }}
          disabled={!assignedDevices.length}
        >
          {assignedDevices.map((device) => (
            <MenuItem key={getDeviceId(device)} value={getDeviceId(device)}>
              {getDeviceName(device)}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          placeholder="Search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: "#fff",
              borderRadius: 1.5,
              fontSize: 16,
              fontWeight: 700,
              pr: 1,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon sx={{ color: "#ff5f7e", fontSize: 36 }} />
              </InputAdornment>
            ),
          }}
          disabled={!assignedDevices.length}
        />

        {!assignedDevices.length ? (
          <Typography sx={{ py: 3, textAlign: "center", color: "#6b7280", fontWeight: 600 }}>
            No assigned devices found for this user.
          </Typography>
        ) : isFetching ? (
          <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: "#dbe4f0", p: 1, maxHeight: 400, overflow: "auto" }}>
          <Stack spacing={0.75}>
            {rows.map((item) => {
              const saving = busyKey === item.key;
              return (
                <Box
                  key={item.key}
                  sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 46 }}
                >
                  <Checkbox
                    checked={Boolean(item.checked)}
                    disabled={saving}
                    onChange={(event) => handleToggle(item, event.target.checked)}
                    sx={{
                      p: 0.5,
                      color: "#1976d2",
                      "&.Mui-checked": { color: "#1976d2" },
                    }}
                  />
                  <Typography sx={{ fontSize: 22, lineHeight: 1.2, color: "#1f2937", fontWeight: 500 }}>
                    {item.label}
                  </Typography>
                  {item.inherited ? (
                    <Typography sx={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>
                      inherited
                    </Typography>
                  ) : null}
                  {saving ? <CircularProgress size={16} sx={{ ml: 0.75 }} /> : null}
                </Box>
              );
            })}

            {!rows.length ? (
              <Typography sx={{ py: 3, textAlign: "center", color: "#6b7280", fontWeight: 600 }}>
                No notification templates configured for this device yet.
              </Typography>
            ) : null}
          </Stack>
          </Paper>
        )}
      </Stack>
    </BrandDialogFrame>
  );
}
